/**
 * Users Page
 * Gerencia usuários dos tenants (control) e usuários do Vover Admin (admin_users).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Chip,
  Grid
} from '@mui/material';
import { VpnKey, Add, Edit } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useFilters } from '@/contexts/FilterContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useAuth } from '@/contexts/AuthContext';
import { StatusToggle } from '@/components/StatusToggle';
import { ClientCell } from '@/components/ClientCell';
import { ProductCell } from '@/components/ProductCell';
import { useConfirmation } from '@/contexts/ConfirmationContext';
import { useMessage } from '@/contexts/MessageContext';
import { useProductsMap } from '@/hooks/useProductsMap';
import { logger } from '@/utils/logger';
import { COPY } from '@/config/copy';

const ADMIN_ROLES: { value: string; label: string }[] = [
  { value: 'super_admin', label: COPY.roles.super_admin },
  { value: 'admin', label: COPY.roles.admin },
  { value: 'operator', label: COPY.roles.operator },
  { value: 'auditor', label: COPY.roles.auditor }
];

interface AdminUserRow {
  _id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  twoFactorEnabled: boolean;
  productScopes?: string[];
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

interface UsersPageProps {
  defaultTab?: 'tenant' | 'admin';
  showTenantUsersTab?: boolean;
  showAdminUsersTab?: boolean;
}

export const UsersPage: React.FC<UsersPageProps> = ({
  defaultTab = 'tenant',
  showTenantUsersTab = true,
  showAdminUsersTab = false
}) => {
  const { admin: currentAdmin } = useAuth();
  const isSuperAdmin = currentAdmin?.role === 'super_admin';

  const [tab, setTab] = useState(defaultTab === 'admin' ? 1 : 0);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const { selectedClients, productId, setProductId } = useFilters();
  const { setRefreshCallback } = useRefresh();
  const { confirm } = useConfirmation();
  const { showSuccess, showError } = useMessage();
  const productsById = useProductsMap();

  const [adminUsers, setAdminUsers] = useState<AdminUserRow[]>([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUsersPage, setAdminUsersPage] = useState(0);
  const [adminUsersRowsPerPage, setAdminUsersRowsPerPage] = useState(20);
  const [adminUsersTotal, setAdminUsersTotal] = useState(0);
  const [tenantOptions, setTenantOptions] = useState<any[]>([]);
  const [tenantOptionsLoading, setTenantOptionsLoading] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState(selectedClients[0] || '');

  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminDialogMode, setAdminDialogMode] = useState<'create' | 'edit'>('create');
  const [adminDialogId, setAdminDialogId] = useState<string | null>(null);
  const [adminDialogForm, setAdminDialogForm] = useState({
    email: '',
    name: '',
    role: 'operator',
    password: '',
    productScopes: 'vover-app'
  });
  const [adminDialogSaving, setAdminDialogSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getUsers({
        page: page + 1,
        limit: rowsPerPage,
        productId,
        tenantId: selectedTenantId || undefined
      });
      if (response.data.success) {
        setUsers(response.data.data);
        setTotal(response.data.pagination?.total ?? 0);
      }
    } catch (error) {
      logger.error('Error loading users', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, productId, selectedTenantId]);

  const loadTenantOptions = useCallback(async () => {
    setTenantOptionsLoading(true);
    try {
      const response = await apiService.getTenants({ page: 1, limit: 500, productId });
      if (response.data?.success) {
        setTenantOptions(response.data.data ?? []);
      }
    } catch (error) {
      logger.error('Error loading tenant options', error);
      setTenantOptions([]);
    } finally {
      setTenantOptionsLoading(false);
    }
  }, [productId]);

  const loadAdminUsers = useCallback(async () => {
    if (!isSuperAdmin) return;
    setAdminUsersLoading(true);
    try {
      const response = await apiService.getAdminUsers({
        page: adminUsersPage + 1,
        limit: adminUsersRowsPerPage
      });
      if (response.data.success) {
        setAdminUsers(response.data.data ?? []);
        setAdminUsersTotal(response.data.pagination?.total ?? 0);
      }
    } catch (error) {
      logger.error('Error loading admin users', error);
    } finally {
      setAdminUsersLoading(false);
    }
  }, [isSuperAdmin, adminUsersPage, adminUsersRowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [selectedClients, productId, selectedTenantId]);

  useEffect(() => {
    if (!showTenantUsersTab && showAdminUsersTab) setTab(1);
    if (!showAdminUsersTab && showTenantUsersTab) setTab(0);
  }, [showTenantUsersTab, showAdminUsersTab]);

  useEffect(() => {
    setSelectedTenantId(selectedClients[0] || '');
  }, [selectedClients]);

  useEffect(() => {
    loadTenantOptions();
  }, [loadTenantOptions]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (tab === 1 && isSuperAdmin) {
      loadAdminUsers();
    }
  }, [tab, isSuperAdmin, loadAdminUsers]);

  useEffect(() => {
    const callback = tab === 0 ? loadUsers : loadAdminUsers;
    setRefreshCallback(callback);
    return () => setRefreshCallback(null);
  }, [tab, loadUsers, loadAdminUsers, setRefreshCallback]);

  const handleStatusToggle = (isActive: boolean, userId: string, tenantId: string) => {
    const action = isActive ? 'desativar' : 'ativar';
    confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} usuário`,
      message: `Tem certeza que deseja ${action} este usuário?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancelar',
      confirmColor: isActive ? 'error' : 'primary',
      onConfirm: async () => {
        try {
          await apiService.toggleUserStatus(userId, { tenantId, isActive: !isActive });
          showSuccess(`Usuário ${!isActive ? 'ativado' : 'desativado'} com sucesso`);
          loadUsers();
        } catch (error: any) {
          showError(error.response?.data?.message || 'Erro ao alterar status do usuário');
        }
      }
    });
  };

  const handleResetPassword = (user: {
    _id: string;
    tenantId: string;
    email?: string;
    name?: string;
  }) => {
    const emailInfo = user.email ? ` O link será enviado para ${user.email}.` : '';
    confirm({
      title: 'Enviar link de redefinição de senha',
      message: `Deseja enviar um link de redefinição de senha para este usuário?${emailInfo} O link é o mesmo fluxo do "Esqueci minha senha" do login do Vover.`,
      confirmText: 'Enviar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          await apiService.resetUserPassword(user._id, { tenantId: user.tenantId });
          showSuccess(
            user.email ? `Link enviado para ${user.email}.` : 'Link de redefinição enviado.'
          );
        } catch (error: any) {
          showError(error.response?.data?.message || 'Erro ao enviar link');
        }
      }
    });
  };

  const openCreateAdmin = () => {
    setAdminDialogMode('create');
    setAdminDialogId(null);
    setAdminDialogForm({
      email: '',
      name: '',
      role: 'operator',
      password: '',
      productScopes: 'vover-app'
    });
    setAdminDialogOpen(true);
  };

  const openEditAdmin = (row: AdminUserRow) => {
    setAdminDialogMode('edit');
    setAdminDialogId(row._id);
    setAdminDialogForm({
      email: row.email,
      name: row.name,
      role: row.role,
      password: '',
      productScopes:
        (row as AdminUserRow & { productScopes?: string[] }).productScopes?.join(', ') ||
        'vover-app'
    });
    setAdminDialogOpen(true);
  };

  const handleSaveAdminUser = async () => {
    if (adminDialogMode === 'create') {
      if (
        !adminDialogForm.email.trim() ||
        !adminDialogForm.name.trim() ||
        !adminDialogForm.password
      ) {
        showError('Preencha e-mail, nome e senha.');
        return;
      }
    } else {
      if (!adminDialogForm.name.trim()) {
        showError('Nome é obrigatório.');
        return;
      }
    }
    setAdminDialogSaving(true);
    try {
      if (adminDialogMode === 'create') {
        await apiService.createAdminUser({
          email: adminDialogForm.email.trim(),
          password: adminDialogForm.password,
          name: adminDialogForm.name.trim(),
          role: adminDialogForm.role,
          productScopes: adminDialogForm.productScopes
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        });
        showSuccess('Usuário admin criado com sucesso.');
      } else if (adminDialogId) {
        const payload: {
          name: string;
          role: string;
          password?: string;
          productScopes?: string[];
        } = {
          name: adminDialogForm.name.trim(),
          role: adminDialogForm.role,
          productScopes: adminDialogForm.productScopes
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        };
        if (adminDialogForm.password) payload.password = adminDialogForm.password;
        await apiService.updateAdminUser(adminDialogId, payload);
        showSuccess('Usuário admin atualizado com sucesso.');
      }
      setAdminDialogOpen(false);
      loadAdminUsers();
    } catch (error: any) {
      showError(error.response?.data?.message || 'Erro ao salvar.');
    } finally {
      setAdminDialogSaving(false);
    }
  };

  const handleAdminUserStatusToggle = (isActive: boolean, id: string) => {
    const action = isActive ? 'inativar' : 'ativar';
    confirm({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} usuário admin`,
      message: `Tem certeza que deseja ${action} este usuário?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancelar',
      confirmColor: isActive ? 'error' : 'primary',
      onConfirm: async () => {
        try {
          await apiService.setAdminUserActive(id, !isActive);
          showSuccess(`Usuário ${!isActive ? 'ativado' : 'inativado'} com sucesso.`);
          loadAdminUsers();
        } catch (error: any) {
          showError(error.response?.data?.message || 'Erro ao alterar status.');
        }
      }
    });
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'action.hover' }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="h4">{COPY.users.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              Gestão de usuários finais do produto selecionado e usuários administrativos do control
              plane. Usuários finais dependem da API interna do produto e exigem exatamente um
              cliente selecionado.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <ProductCell
                productId={productId}
                name={productsById.get(productId)?.displayName}
                avatarUrl={productsById.get(productId)?.avatarUrl}
              />
              <Chip
                size="small"
                variant="outlined"
                label={
                  selectedClients.length === 1
                    ? '1 cliente selecionado'
                    : `${selectedClients.length} clientes selecionados`
                }
              />
              <Chip size="small" variant="outlined" label="Fonte: API interna do produto" />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {(showTenantUsersTab || (showAdminUsersTab && isSuperAdmin)) && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          {showTenantUsersTab && (
            <Tab label={COPY.users.tabTenantUsers} id="users-tab-0" value={0} />
          )}
          {showAdminUsersTab && isSuperAdmin && (
            <Tab label={COPY.users.tabAdminUsers} id="users-tab-1" value={1} />
          )}
        </Tabs>
      )}

      {showTenantUsersTab && tab === 0 && (
        <>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Produto</InputLabel>
                    <Select
                      value={productId}
                      label="Produto"
                      onChange={e => {
                        setProductId(e.target.value as string);
                        setSelectedTenantId('');
                      }}
                    >
                      {Array.from(productsById.values()).map(product => (
                        <MenuItem key={product.productId} value={product.productId}>
                          <ProductCell
                            productId={product.productId}
                            name={product.displayName}
                            avatarUrl={product.avatarUrl}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Cliente</InputLabel>
                    <Select
                      value={selectedTenantId}
                      label="Cliente"
                      onChange={e => setSelectedTenantId(e.target.value as string)}
                      disabled={tenantOptionsLoading}
                    >
                      <MenuItem value="">
                        <em>Selecione um cliente</em>
                      </MenuItem>
                      {tenantOptions.map(tenant => (
                        <MenuItem key={tenant.tenantId} value={tenant.tenantId}>
                          <ClientCell
                            name={tenant.companyName}
                            tenantId={tenant.tenantId}
                            logoUrl={tenant.logoUrl}
                            size="small"
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {!selectedTenantId && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Selecione um produto e um cliente para listar usuários. A listagem vem da API interna
              do produto, não de uma varredura direta no Mongo do admin.
            </Alert>
          )}
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {COPY.common.loading}
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {!selectedTenantId
                        ? 'Selecione um produto e um cliente para carregar usuários.'
                        : COPY.users.noTenantUsers}
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map(user => (
                    <TableRow key={`${user._id}-${user.tenantId}`}>
                      <TableCell>
                        <ProductCell
                          productId={productId}
                          name={productsById.get(productId)?.displayName}
                          avatarUrl={productsById.get(productId)?.avatarUrl}
                        />
                      </TableCell>
                      <TableCell>{user.name || '-'}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <ClientCell name={user.tenantName} tenantId={user.tenantId} size="small" />
                      </TableCell>
                      <TableCell>
                        <StatusToggle
                          isActive={user.isActive}
                          onClick={isActive =>
                            handleStatusToggle(isActive, user._id, user.tenantId)
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleResetPassword(user)}
                          title="Enviar link de redefinição de senha"
                        >
                          <VpnKey />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </>
      )}

      {showAdminUsersTab && tab === 1 && isSuperAdmin && (
        <>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">{COPY.users.tabAdminUsers}</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openCreateAdmin}>
              {COPY.users.newAdminUser}
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Perfil</TableCell>
                  <TableCell>Produtos</TableCell>
                  <TableCell>2FA</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adminUsersLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {COPY.common.loading}
                    </TableCell>
                  </TableRow>
                ) : adminUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {COPY.users.noAdminUsers}
                    </TableCell>
                  </TableRow>
                ) : (
                  adminUsers.map(row => (
                    <TableRow key={row._id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>
                        {ADMIN_ROLES.find(r => r.value === row.role)?.label ?? row.role}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {(row.productScopes?.length ? row.productScopes : ['vover-app']).map(
                            scope =>
                              scope === '*' ? (
                                <Chip key={scope} size="small" color="primary" label="Todos" />
                              ) : (
                                <ProductCell
                                  key={scope}
                                  productId={scope}
                                  name={productsById.get(scope)?.displayName}
                                  avatarUrl={productsById.get(scope)?.avatarUrl}
                                />
                              )
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{row.twoFactorEnabled ? 'Sim' : 'Não'}</TableCell>
                      <TableCell>
                        <StatusToggle
                          isActive={row.isActive}
                          onClick={isActive => handleAdminUserStatusToggle(isActive, row._id)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEditAdmin(row)} title="Editar">
                          <Edit />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={adminUsersTotal}
            page={adminUsersPage}
            onPageChange={(_, newPage) => setAdminUsersPage(newPage)}
            rowsPerPage={adminUsersRowsPerPage}
            onRowsPerPageChange={e => {
              setAdminUsersRowsPerPage(parseInt(e.target.value, 10));
              setAdminUsersPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </>
      )}

      <Dialog
        open={adminDialogOpen}
        onClose={() => setAdminDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {adminDialogMode === 'create' ? COPY.users.createAdminUser : COPY.users.editAdminUser}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="E-mail"
              type="email"
              value={adminDialogForm.email}
              onChange={e => setAdminDialogForm(prev => ({ ...prev, email: e.target.value }))}
              disabled={adminDialogMode === 'edit'}
              fullWidth
              required
            />
            <TextField
              label="Nome"
              value={adminDialogForm.name}
              onChange={e => setAdminDialogForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Perfil</InputLabel>
              <Select
                value={adminDialogForm.role}
                label="Perfil"
                onChange={e =>
                  setAdminDialogForm(prev => ({
                    ...prev,
                    role: e.target.value as string
                  }))
                }
              >
                {ADMIN_ROLES.map(r => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={adminDialogMode === 'edit' ? COPY.users.passwordOptionalHint : 'Senha'}
              type="password"
              value={adminDialogForm.password}
              onChange={e => setAdminDialogForm(prev => ({ ...prev, password: e.target.value }))}
              fullWidth
              required={adminDialogMode === 'create'}
              helperText={adminDialogMode === 'create' ? COPY.users.passwordHint : undefined}
            />
            {adminDialogForm.role !== 'super_admin' && (
              <TextField
                label="Produtos (escopo)"
                value={adminDialogForm.productScopes}
                onChange={e =>
                  setAdminDialogForm(prev => ({ ...prev, productScopes: e.target.value }))
                }
                fullWidth
                helperText="CSV: vover-app, vover-ibody ou * (apenas super_admin no backend)"
                disabled={adminDialogForm.role === 'super_admin'}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminDialogOpen(false)}>{COPY.common.cancel}</Button>
          <Button variant="contained" onClick={handleSaveAdminUser} disabled={adminDialogSaving}>
            {adminDialogSaving ? COPY.common.saving : COPY.common.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
